import { XMarkIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";

import { Badge, Button, Headline, Paragraph, Small } from "@fronterahealth/frontera-ui-components";

import { ProviderType, useSendCaregiverActivationCodeNotificationsMutation } from "@api/graphql/types-and-hooks";
import { TableFilterDropdown } from "@components/TableFilterDropdown/TableFilterDropdown";
import { notifyError, notifySuccess } from "@components/notifications/notifications";
import { useAllCaregivers } from "@hooks/useAllCaregivers";
import { CaregiverAppStatus, MemberCaregiver } from "@pages/Members/types";
import { buildBcbaNameByProviderId, mapCaregiverToMemberCaregiver } from "@pages/Members/utils/caregiverMapping";
import { useAdminData } from "@providers/AdminDataProvider";
import { useProvidersData } from "@providers/ProvidersDataProvider";

interface BulkInviteCaregiversModalProps {
  isOpen: boolean;
  onClose: () => void;
}

enum Step {
  Filter = "filter",
  Review = "review",
}

const STATUS_OPTIONS: { value: CaregiverAppStatus; label: string }[] = [
  { value: CaregiverAppStatus.NotInvited, label: "Not invited" },
  { value: CaregiverAppStatus.CodeSent, label: "Code sent (resend)" },
];

// Sends happen asynchronously via a Temporal workflow — there's no progress
// tracking, so poll for a while after queuing rather than assuming the rows have updated by any single point in time.
const POLL_INTERVAL_MS = 10_000;
const POLL_DURATION_MS = 2 * 60 * 1000;

const getDeliveryChannelsLabel = (hasEmail: boolean, hasPhone: boolean) => {
  if (hasEmail && hasPhone) return "email & SMS";
  if (hasEmail) return "email";
  return "SMS";
};

export const BulkInviteCaregiversModal: React.FC<BulkInviteCaregiversModalProps> = ({ isOpen, onClose }) => {
  const { roles } = useAdminData();
  const [step, setStep] = useState<Step>(Step.Filter);
  const [selectedStatuses, setSelectedStatuses] = useState<CaregiverAppStatus[]>([CaregiverAppStatus.NotInvited]);
  const [selectedBcbas, setSelectedBcbas] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isPollingForResults, setIsPollingForResults] = useState(false);

  const { providersQuery } = useProvidersData();
  const queryClient = useQueryClient();

  const {
    caregivers: allCaregivers,
    isLoading: caregiversLoading,
    isFetchingMore,
  } = useAllCaregivers({
    enabled: roles.includes("admin"),
  });

  useEffect(() => {
    if (!isPollingForResults) return;
    const interval = setInterval(
      () => queryClient.invalidateQueries({ queryKey: ["getCaregiversDataQuery"] }),
      POLL_INTERVAL_MS,
    );
    const timeout = setTimeout(() => setIsPollingForResults(false), POLL_DURATION_MS);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isPollingForResults, queryClient]);

  const bcbaNameByProviderId = useMemo(
    () => buildBcbaNameByProviderId((providersQuery.data?.provider || []) as ProviderType[]),
    [providersQuery.data],
  );

  const caregivers = useMemo(
    () =>
      allCaregivers
        .map((caregiver) => mapCaregiverToMemberCaregiver(caregiver, bcbaNameByProviderId))
        .filter((c): c is MemberCaregiver => c !== null),
    [allCaregivers, bcbaNameByProviderId],
  );

  const bcbaFilterItems = useMemo(() => {
    const names = new Set(caregivers.map((c) => c.assignedBcba).filter(Boolean));
    return Array.from(names).map((name) => ({ id: name, label: name }));
  }, [caregivers]);

  const filteredCaregivers = useMemo(() => {
    return caregivers.filter((c) => {
      // Connected caregivers are never a valid invite target, regardless of status filter.
      if (c.appStatus === CaregiverAppStatus.Connected) return false;
      const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(c.appStatus);
      const bcbaMatch = selectedBcbas.length === 0 || selectedBcbas.includes(c.assignedBcba);
      return statusMatch && bcbaMatch;
    });
  }, [caregivers, selectedStatuses, selectedBcbas]);

  const selectableCaregivers = useMemo(
    () => filteredCaregivers.filter((c) => c.email || c.phone),
    [filteredCaregivers],
  );

  // Full reset on filter change, additive-only as more caregivers stream in.
  const filterKey = `${selectedStatuses.join(",")}|${selectedBcbas.join(",")}`;
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(selectableCaregivers.map((c) => c.id)));
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey);
  const [prevSelectableCaregivers, setPrevSelectableCaregivers] = useState(selectableCaregivers);
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey);
    setPrevSelectableCaregivers(selectableCaregivers);
    setSelectedIds(new Set(selectableCaregivers.map((c) => c.id)));
  } else if (selectableCaregivers !== prevSelectableCaregivers) {
    setPrevSelectableCaregivers(selectableCaregivers);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      selectableCaregivers.forEach((c) => next.add(c.id));
      return next;
    });
  }

  const sendActivationCodes = useSendCaregiverActivationCodeNotificationsMutation({
    onSuccess: (res) => {
      if (!res.sendCaregiverActivationCodeNotifications?.status) {
        notifyError(res.sendCaregiverActivationCodeNotifications?.message || "Something went wrong queuing the codes.");
        return;
      }
      notifySuccess(res.sendCaregiverActivationCodeNotifications?.message || "Activation codes are being sent.");
      setIsPollingForResults(true);
      handleClose();
    },
    onError: () => {
      notifyError("Something went wrong queuing the activation codes.");
    },
  });

  const handleClose = () => {
    setStep(Step.Filter);
    setSelectedStatuses([CaregiverAppStatus.NotInvited]);
    setSelectedBcbas([]);
    setSearchQuery("");
    setSelectedIds(new Set());
    onClose();
  };

  const searchedCaregivers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return filteredCaregivers;
    return filteredCaregivers.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.email && c.email.toLowerCase().includes(q)) ||
        (c.phone && c.phone.toLowerCase().includes(q)),
    );
  }, [filteredCaregivers, searchQuery]);

  const toggleStatus = (status: CaregiverAppStatus) => {
    setSelectedStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  };

  const toggleCaregiver = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === selectableCaregivers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableCaregivers.map((c) => c.id)));
    }
  };

  const selectedCaregivers = filteredCaregivers.filter((c) => selectedIds.has(c.id));

  const handleSendInvites = () => {
    if (selectedCaregivers.length === 0) return;
    sendActivationCodes.mutate({ caregiverIds: selectedCaregivers.map((c) => c.id) });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="relative flex max-h-[85vh] w-full max-w-4xl flex-col rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-limestone-200 px-6 py-4">
          <Headline as="h2" size="small">
            {step === Step.Filter ? "Bulk invite caregivers" : "Review & send"}
          </Headline>
          <button
            onClick={handleClose}
            className="cursor-pointer text-text-secondary hover:text-text-primary"
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Step 1: Filter cohort */}
        {step === Step.Filter && (
          <>
            {/* Filters — outside scroll area so dropdown isn't clipped */}
            <div className="shrink-0 px-6 pt-4">
              <div className="flex items-center gap-x-5 gap-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-text-secondary">Status</span>
                  <div className="flex items-center gap-1.5">
                    {STATUS_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => toggleStatus(opt.value)}
                        className={
                          selectedStatuses.includes(opt.value)
                            ? "rounded-full border border-rio-600 bg-rio-50 px-3 py-1 text-xs font-medium text-rio-700 transition-colors"
                            : "rounded-full border border-border-medium bg-white px-3 py-1 text-xs font-medium text-text-secondary transition-colors hover:border-limestone-300 hover:bg-limestone-50"
                        }
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ml-auto flex items-center gap-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by name, email, or phone"
                    className="w-56 rounded-md border border-border-medium px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:border-rio-600 focus:outline-none"
                  />
                  <TableFilterDropdown<string>
                    label="BCBA"
                    items={bcbaFilterItems}
                    value={selectedBcbas}
                    onChange={setSelectedBcbas}
                  />
                </div>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col px-6 py-3">
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border-strong bg-bg-surface">
                {/* Table preview */}
                <div className="flex shrink-0 items-center gap-2 border-b border-border-strong bg-bg-default px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === selectableCaregivers.length && selectableCaregivers.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 rounded border-limestone-300 text-rio-600 focus:ring-rio-600"
                    disabled={selectableCaregivers.length === 0}
                  />
                  <span className="text-xs font-medium text-text-secondary">
                    {selectedIds.size} of {selectableCaregivers.length} selected
                  </span>
                  {isFetchingMore && <span className="text-xs text-text-secondary">Loading caregivers…</span>}
                </div>
                <div
                  className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden"
                  style={{ scrollbarWidth: "none" }}
                >
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 z-10 border-b border-border-strong bg-bg-default">
                      <tr>
                        <th className="w-10 px-3 py-2" />
                        <th className="px-3 py-2 text-xs font-medium text-text-secondary">Name</th>
                        <th className="px-3 py-2 text-xs font-medium text-text-secondary">Contact</th>
                        <th className="px-3 py-2 text-xs font-medium text-text-secondary">BCBA</th>
                        <th className="w-32 px-3 py-2 text-xs font-medium text-text-secondary">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-subtle">
                      {searchedCaregivers.map((caregiver) => {
                        const hasContact = Boolean(caregiver.email || caregiver.phone);
                        return (
                          <tr key={caregiver.id} className={hasContact ? "bg-white" : "bg-white opacity-50"}>
                            <td className="w-10 px-3 py-2">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(caregiver.id)}
                                onChange={() => toggleCaregiver(caregiver.id)}
                                className="h-4 w-4 rounded border-limestone-300 text-rio-600 focus:ring-rio-600"
                                disabled={!hasContact}
                              />
                            </td>
                            <td className="px-3 py-2">
                              <div className="font-medium text-text-primary">{caregiver.name}</div>
                              <div className="text-xs text-text-secondary">
                                {caregiver.linkedChildren.map((c) => c.name).join(", ") || "No linked child"}
                              </div>
                            </td>
                            <td className="px-3 py-2">
                              {hasContact ? (
                                <>
                                  <div className="text-text-secondary">{caregiver.email || "—"}</div>
                                  {caregiver.phone && (
                                    <div className="text-xs text-text-secondary">{caregiver.phone}</div>
                                  )}
                                </>
                              ) : (
                                <div className="text-text-secondary">No contact info</div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-text-secondary">{caregiver.assignedBcba || "—"}</td>
                            <td className="w-32 whitespace-nowrap px-3 py-2">
                              {caregiver.appStatus === CaregiverAppStatus.NotInvited && (
                                <Badge appearance="neutral" text="Not invited" />
                              )}
                              {caregiver.appStatus === CaregiverAppStatus.CodeSent && (
                                <Badge appearance="warn" text="Code sent" />
                              )}
                              {caregiver.appStatus === CaregiverAppStatus.Connected && (
                                <Badge appearance="success" text="Connected" />
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {searchedCaregivers.length === 0 && (
                    <Paragraph colorType="secondary" className="px-3 py-6 text-center">
                      {caregiversLoading ? "Loading caregivers…" : "No caregivers match the selected filters"}
                    </Paragraph>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex shrink-0 justify-end gap-3 border-t border-limestone-200 px-6 py-4">
              <Button variant="primary" buttonStyle="border" text="Cancel" onClick={handleClose} />
              <Button
                variant="primary"
                buttonStyle="filled"
                text={isFetchingMore ? "Loading caregivers…" : "Next"}
                onClick={() => setStep(Step.Review)}
                disabled={selectedIds.size === 0 || isFetchingMore}
              />
            </div>
          </>
        )}

        {/* Step 2: Review & send */}
        {step === Step.Review && (
          <>
            <div className="flex min-h-0 flex-1 flex-col gap-4 px-6 py-4">
              <div className="shrink-0 rounded-md border border-limestone-200 bg-limestone-50 p-4">
                <Paragraph colorType="primary">
                  <span className="font-medium">
                    Sending activation codes to {selectedCaregivers.length} caregiver
                    {selectedCaregivers.length !== 1 ? "s" : ""} via email and/or SMS.
                  </span>
                </Paragraph>
              </div>

              {selectedCaregivers.length > 0 && (
                <div className="flex min-h-0 flex-1 flex-col">
                  <Small displayType="loud" colorType="primary" className="mb-2 shrink-0">
                    Will receive activation codes:
                  </Small>
                  <ul
                    className="divide-y divide-border-subtle overflow-y-auto rounded-md border border-limestone-200 [&::-webkit-scrollbar]:hidden"
                    style={{ scrollbarWidth: "none" }}
                  >
                    {selectedCaregivers.map((c) => (
                      <li key={c.id} className="flex items-center justify-between px-3 py-2">
                        <div>
                          <Small displayType="loud" colorType="primary">
                            {c.name}
                          </Small>
                          <Small colorType="secondary" className="text-xs">
                            {c.linkedChildren.map((ch) => ch.name).join(", ")}
                          </Small>
                        </div>
                        <div className="text-right">
                          <Small colorType="secondary" className="text-xs">
                            {[c.email, c.phone].filter(Boolean).join(" · ")}
                          </Small>
                          <Small colorType="secondary" className="text-xs">
                            via {getDeliveryChannelsLabel(!!c.email, !!c.phone)}
                          </Small>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex shrink-0 justify-end gap-3 border-t border-limestone-200 px-6 py-4">
              <Button
                variant="primary"
                buttonStyle="border"
                text="Back"
                onClick={() => setStep(Step.Filter)}
                disabled={sendActivationCodes.isPending}
              />
              <Button
                variant="primary"
                buttonStyle="filled"
                text="Send activation codes"
                onClick={handleSendInvites}
                disabled={selectedCaregivers.length === 0 || sendActivationCodes.isPending}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
